/**
 * Web Google Ads capture slots (English). Logo stays at /ad/company-logo (512×512).
 * @see docs/todo/260929-web-ad-plan.md
 */

export const WEB_AD_FRAME = {
    landscape: { id: "landscape", label: "Landscape", ratio: "1.91:1", width: 1200, height: 628 },
    square: { id: "square", label: "Square", ratio: "1:1", width: 1200, height: 1200 },
};

/** Live jigsaw for web ads — same UUID as `/puzzle/play-jigsaw/:storyId`. */
export const WEB_AD_JIGSAW_STORY_ID = "e24a914a-9984-4ab9-9d2f-ab84beb1d53b";
/** Prefer 6×6 so pieces stay readable in 1200px creatives. */
export const WEB_AD_JIGSAW_EXPECTED_GRID = 6;

/** Live word wheel for web ads — admin tpl detail id. */
export const WEB_AD_WORD_WHEEL_TPL_ID = "ac63d16d-eacc-465c-8d57-cb1187c06629";

/** Live crossword for web ads — same UUID as `/puzzle/play-cross/:id`. */
export const WEB_AD_CROSSWORD_ID = "2af92a21-05c1-4b19-91c2-4b4967bfcc1e";

/** Tutor story for web ads — "The Gallery Deception" (english). */
export const WEB_AD_TUTOR_STORY_ID = "ea760636-40d4-4ca4-b5c0-128618ea5e97";
export const WEB_AD_TUTOR_STORY_TITLE = "The Gallery Deception";

/** Display order for gallery / WEB tab sections. */
export const WEB_AD_GROUPS = ["Jigsaw", "Word Wheel", "Crossword", "Tutor"];

/** @typedef {{ id: string, asset: 'landscape'|'square', label: string, group: string, headline: string, sub: string, fileStem: string }} WebAdShot */

/** @type {WebAdShot[]} — ordered by app group, then landscape before square */
export const WEB_AD_SHOTS = [
    {
        id: "jigsaw",
        asset: "landscape",
        label: "Jigsaw — landscape",
        group: "Jigsaw",
        headline: "Piece the picture together",
        sub: "Board, tray, and a calm play space",
        fileStem: "web-ad-jigsaw-landscape",
    },
    {
        id: "jigsaw",
        asset: "square",
        label: "Jigsaw — square",
        group: "Jigsaw",
        headline: "Every piece finds its place",
        sub: "A board-forward take on jigsaw",
        fileStem: "web-ad-jigsaw-square",
    },
    {
        id: "word-wheel",
        asset: "landscape",
        label: "Word Wheel — landscape",
        group: "Word Wheel",
        headline: "Spin letters. Find every word.",
        sub: "Grid and wheel in one glance",
        fileStem: "web-ad-word-wheel-landscape",
    },
    {
        id: "word-wheel",
        asset: "square",
        label: "Word Wheel — square",
        group: "Word Wheel",
        headline: "One wheel. Many words.",
        sub: "Wheel-first play energy",
        fileStem: "web-ad-word-wheel-square",
    },
    {
        id: "crossword",
        asset: "landscape",
        label: "Crossword — landscape",
        group: "Crossword",
        headline: "Classic clues. Fresh grids.",
        sub: "Fill the board one letter at a time",
        fileStem: "web-ad-crossword-landscape",
    },
    {
        id: "crossword",
        asset: "square",
        label: "Crossword — square",
        group: "Crossword",
        headline: "Solve across and down",
        sub: "Grid-forward crossword focus",
        fileStem: "web-ad-crossword-square",
    },
    {
        id: "tutor-content",
        asset: "landscape",
        label: "Tutor — put your content",
        group: "Tutor",
        headline: "Put your content in",
        sub: `${WEB_AD_TUTOR_STORY_TITLE} — story image and words`,
        fileStem: "web-ad-tutor-content",
    },
    {
        id: "tutor-make",
        asset: "landscape",
        label: "Tutor — make a puzzle",
        group: "Tutor",
        headline: "Make a puzzle in minutes",
        sub: `Turn ${WEB_AD_TUTOR_STORY_TITLE} into playable boards`,
        fileStem: "web-ad-tutor-make",
    },
    {
        id: "tutor-print",
        asset: "landscape",
        label: "Tutor — print & play online",
        group: "Tutor",
        headline: "Print it. Play it online.",
        sub: `${WEB_AD_TUTOR_STORY_TITLE} on paper or on the screen`,
        fileStem: "web-ad-tutor-print",
    },
];

/** @returns {{ group: string, shots: WebAdShot[] }[]} */
export function webAdShotsByGroup() {
    return WEB_AD_GROUPS.map((group) => ({
        group,
        shots: WEB_AD_SHOTS.filter((shot) => shot.group === group),
    })).filter((section) => section.shots.length > 0);
}

export function findWebAdShot(asset, shotId) {
    const a = String(asset || "landscape").trim();
    const s = String(shotId || "").trim();
    if (!s) {
        return WEB_AD_SHOTS.find((item) => item.asset === a) || WEB_AD_SHOTS[0];
    }
    return (
        WEB_AD_SHOTS.find((item) => item.asset === a && item.id === s)
        || WEB_AD_SHOTS.find((item) => item.id === s)
        || WEB_AD_SHOTS[0]
    );
}

export function webAdShotQueryPath(shot) {
    return `/ad/web-google-ads?asset=${encodeURIComponent(shot.asset)}&shot=${encodeURIComponent(shot.id)}`;
}
